/**
 * Pulse — concentric expanding rings at a point on the globe surface.
 *
 * Used to mark an incident target (e.g. the DDoS swarm's victim PoP): a few
 * rings ripple outward and fade on a loop, additive-blended for a hot glow.
 * Oriented tangent to the sphere at (lat, lon) so the rings lie flat on the
 * surface and rotate with the globe. Render as a child of `<Globe>`.
 */

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import {
  AdditiveBlending,
  Color,
  DoubleSide,
  type Mesh,
  MeshBasicMaterial,
  Quaternion,
  RingGeometry,
  Vector3,
} from 'three';

import { latLonToVec3 } from './coords.js';

export interface PulseProps {
  readonly lat: number;
  readonly lon: number;
  readonly radius?: number;
  readonly color?: string | number;
  /** Max ring radius in scene units. Default 0.12. */
  readonly size?: number;
  /** Seconds per ripple. Default 1.3. */
  readonly period?: number;
  /** Number of overlapping ripples. Default 3. */
  readonly count?: number;
}

const LOCAL_Z = new Vector3(0, 0, 1);

export function Pulse({
  lat,
  lon,
  radius = 1,
  color = 0xff5a5a,
  size = 0.12,
  period = 1.3,
  count = 3,
}: PulseProps): JSX.Element {
  // A unit ring (inner 0.72 → outer 1.0); each instance is scaled per frame.
  const geometry = useMemo(() => new RingGeometry(0.72, 1.0, 48), []);

  // One material per ripple so they can fade independently.
  const materials = useMemo(
    () =>
      Array.from(
        { length: count },
        () =>
          new MeshBasicMaterial({
            color: new Color(color),
            transparent: true,
            blending: AdditiveBlending,
            depthWrite: false,
            side: DoubleSide,
          }),
      ),
    [color, count],
  );

  // Fixed position + orientation: lie flat on the surface, lifted a hair.
  const position = useMemo(() => latLonToVec3(lat, lon, radius * 1.02), [lat, lon, radius]);
  const quaternion = useMemo(
    () => new Quaternion().setFromUnitVectors(LOCAL_Z, position.clone().normalize()),
    [position],
  );

  const meshes = useRef<Array<Mesh | null>>([]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    for (let i = 0; i < count; i++) {
      const mesh = meshes.current[i];
      if (!mesh) continue;
      const phase = (t / period + i / count) % 1;
      mesh.scale.setScalar((0.25 + phase * 1.0) * size);
      (mesh.material as MeshBasicMaterial).opacity = Math.max(0, 1 - phase) * 0.9;
    }
  });

  return (
    <group position={position} quaternion={quaternion}>
      {materials.map((material, i) => (
        <mesh
          key={i}
          ref={(el) => {
            meshes.current[i] = el;
          }}
          geometry={geometry}
          material={material}
        />
      ))}
    </group>
  );
}
